import { createContext, useContext, ReactNode, useMemo } from 'react';

// Import types
import type { PacketWithPriceAndChannels, Settings } from 'types/views/configurator';
import type { LastPaymentsRedirectUrl } from 'types/api/payments';
import type { UserSubscriptionParsed } from 'types/views/subscription';

// Import hooks
import { useAppSelector } from 'store/hooks';
import { useConfiguratorSettings } from './useConfiguratorSettings';
import { useResources } from 'components/views/offer_configurator/hooks/useResources';
import { useSummaryStore } from 'components/views/offer_configurator/hooks';

// Import helpers
import { findPacket } from 'components/views/offer_configurator/helpers/packets';

type Props = {
	children: ReactNode;
};

type ContextType = {
	isLoading: boolean;
	subscription: UserSubscriptionParsed | null;
	lastPayment: LastPaymentsRedirectUrl | null;
	selectedMain: PacketWithPriceAndChannels | undefined;
	settings: Settings | null;
	currentMain: PacketWithPriceAndChannels | undefined;
	mainPacketsDescription: string | null;
};

export const ConfiguratorContext = createContext<ContextType>({
	isLoading: false,
	subscription: null,
	lastPayment: null,
	selectedMain: undefined,
	settings: null,
	currentMain: undefined,
	mainPacketsDescription: null
});

export const useConfiguratorContext = () => useContext(ConfiguratorContext);

const ConfiguratorProvider = ({ children }: Props) => {
	const summaryMain = useSummaryStore((state) => state.main);
	const packets = useAppSelector(({ configurator }) => configurator.packets);
	const settings = useConfiguratorSettings();

	const {
		resources: [{ data: lastPayment = null }, { data: subscription = null }, { data: mainPacketsDescription = null }],
		isResourcesLoading
	} = useResources();

	const currentMain = useMemo(() => findPacket(packets.main, subscription?.main.product_uuid), [packets.isLoading, subscription?.main]);

	const selectedMain = useMemo(() => findPacket(packets.main, summaryMain?.uuid), [summaryMain, packets.isLoading]);

	return (
		<ConfiguratorContext.Provider
			value={{
				isLoading: isResourcesLoading || packets.isLoading,
				mainPacketsDescription,
				subscription,
				lastPayment,
				selectedMain,
				settings,
				currentMain
			}}
		>
			{children}
		</ConfiguratorContext.Provider>
	);
};

export default ConfiguratorProvider;
